import { useEffect, useState } from "react";
import QRCode from "react-qr-code";
import useTranslator from "../../../../hooks/useTranslator";
import { MOBILE_SESSION_STATE } from "../mobileSessionState";

interface ContinueOnPhoneScreenProps {
  qrUrl: string;
  copyUrl: string;
  sessionState: string;
  onCancel: () => void;
}

const isTerminalState = (state: string): boolean =>
  [
    MOBILE_SESSION_STATE.COMPLETED,
    MOBILE_SESSION_STATE.FAILED,
    MOBILE_SESSION_STATE.EXPIRED,
    MOBILE_SESSION_STATE.CANCELLED,
  ].includes(state as any);

const statusLabels: Record<string, string> = {
  [MOBILE_SESSION_STATE.PENDING]: "connectPhoneStatusPending",
  [MOBILE_SESSION_STATE.PHONE_OPENED]: "connectPhoneStatusOpened",
  [MOBILE_SESSION_STATE.FRONT_CAPTURED]: "connectPhoneStatusFront",
  [MOBILE_SESSION_STATE.BACK_CAPTURED]: "connectPhoneStatusBack",
  [MOBILE_SESSION_STATE.SUBMITTED]: "connectPhoneStatusSubmitted",
  [MOBILE_SESSION_STATE.COMPLETED]: "connectPhoneStatusCompleted",
  [MOBILE_SESSION_STATE.FAILED]: "connectPhoneStatusFailed",
  [MOBILE_SESSION_STATE.EXPIRED]: "connectPhoneStatusExpired",
  [MOBILE_SESSION_STATE.CANCELLED]: "connectPhoneStatusCancelled",
};

const ContinueOnPhoneScreen = ({
  qrUrl,
  copyUrl,
  sessionState,
  onCancel,
}: ContinueOnPhoneScreenProps) => {
  const { _t } = useTranslator();
  const [isCopied, setIsCopied] = useState(false);

  useEffect(() => {
    if (!isCopied) return;
    const timeout = window.setTimeout(() => setIsCopied(false), 2000);
    return () => window.clearTimeout(timeout);
  }, [isCopied]);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(copyUrl);
    } catch {
      const textArea = document.createElement("textarea");
      textArea.value = copyUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
    }
    setIsCopied(true);
  };

  const isTerminal = isTerminalState(sessionState);

  const isInProgress =
    !isTerminal && sessionState !== MOBILE_SESSION_STATE.PENDING;

  const statusKey = statusLabels[sessionState] || "connectPhoneStatusPending";
  const statusLabel = _t(statusKey);

  return (
    <div className="kyc-verification-start continue-on-phone-screen">
      <div className="kyc-verification-start-media">
        {isTerminal ? (
          <div
            className={`continue-on-phone-terminal-icon ${
              sessionState === MOBILE_SESSION_STATE.COMPLETED
                ? "success"
                : "error"
            }`}
          >
            {sessionState === MOBILE_SESSION_STATE.COMPLETED ? "✓" : "✕"}
          </div>
        ) : (
          <div className="continue-on-phone-qr-wrapper">
            <QRCode
              value={qrUrl}
              size={180}
              level="M"
              bgColor="#ffffff"
              fgColor="#000000"
            />
          </div>
        )}
      </div>

      <div className="kyc-verification-start-copy">
        <h3>
          {isTerminal ? _t("connectPhoneTitleDone", "onfido kyc") : _t("connectPhoneTitle", "onfido kyc")}
        </h3>
        <p>{isTerminal ? statusLabel : _t("connectPhoneDescription", "onfido kyc")}</p>
        {isInProgress && (
          <div
            className="continue-on-phone-status-badge"
            role="status"
            aria-live="polite"
          >
            <div className="continue-on-phone-spinner" aria-hidden="true" />
            <span>{statusLabel}</span>
          </div>
        )}
      </div>

      <div className="kyc-verification-start-actions">
        {!isTerminal && (
          <>
            <button
              type="button"
              className="kyc-verification-start-button"
              onClick={handleCopyLink}
            >
              {isCopied ? _t("Copied", "onfido kyc") : _t("copyLink", "onfido kyc")}
            </button>
            <button
              type="button"
              className="kyc-verification-start-button secondary"
              onClick={onCancel}
            >
              {_t("connectPhoneCancel", "onfido kyc")}
            </button>
          </>
        )}
        {isTerminal && sessionState !== MOBILE_SESSION_STATE.COMPLETED && (
          <button
            type="button"
            className="kyc-verification-start-button"
            onClick={onCancel}
          >
            {_t("connectPhoneGoBack", "onfido kyc")}
          </button>
        )}
      </div>
    </div>
  );
};

export default ContinueOnPhoneScreen;
